import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import landingEmptyLogo from "images/landing-page/landing-empty-logo.png";

import client from "@culturehq/client";
import { classnames, Icon, Loader, Panel, PlainButton } from "@culturehq/components";

import { QueryStringConsumer, QueryStringProvider } from "app/context/QueryStringContext";
import { useDoor, useFetch, useFieldsLabels, useInsideIframe, useOrganizationTitle, useIsTablet } from "app/hooks";
import { canViewCompaniesField } from "app/permissions";
import { getTags, isCentrica, isDeloitte, isPlatformSubdomain, isProgressive, isTarget, t, Trans } from "app/utils";

import ErrorPage from "components/ErrorPage";
import LandingPageStories from "components/admin/LandingPageStories";
import Tags from "components/common/Tags";
import UsersSlider from "components/admin/settings/UsersSlider";
import LazyEditorOutput from "components/editor/LazyEditorOutput";
import NotFound from "components/NotFound";
import { keyToCategory } from "components/post/useTags";
import StoryItemPlaceholder from "components/admin/settings/StoryItemPlaceholder";
import HeaderImage from "components/landingPage/HeaderImage";

const queryToOptions = queryString => {
  const { i, d, l, s, cl, cy, p, de, v, co } = queryString;

  return {
    interestIds: i || null,
    companyIds: co || null,
    departmentIds: d || null,
    locationIds: l || null,
    skillIds: s || null,
    clubIds: cl || null,
    classYearIds: cy || null,
    programIds: p || null,
    degreeIds: de || null,
    organizationValueIds: v || null
  };
};

const queryStringParsers = {
  i: "intList",
  d: "intList",
  l: "intList",
  s: "intList",
  cl: "intList",
  cy: "intList",
  p: "intList",
  de: "intList",
  v: "intList",
  co: "intList"
};

const DEFAULT_COLOR = "#79b17d";

const LandingPageCalloutContext = ({ queryString, orgName }) => {
  const { prefix } = useParams();
  const [tags, setTags] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedDegrees, setSelectedDegrees] = useState([]);
  const [selectedPrograms, setSelectedPrograms] = useState([]);
  const [selectedFiltersNoDepNoLoc, setSelectedFiltersNoDepNoLoc] = useState([]);
  const insideIframe = useInsideIframe();
  const { updateSeoTitle } = useOrganizationTitle();
  const { open: openOnMobile, onToggle: onToggleOpenOnMobile } = useDoor(false);
  const isTablet = useIsTablet();
  const organization = orgName.split("-").join(" ");

  const { error, fetching, json: { landingPage } } = useFetch(
    () => client.listOrganizationLandingPages({
      ...queryToOptions(queryString),
      organization: isPlatformSubdomain(organization) ? undefined : organization,
      route: prefix ? `/${prefix}` : ""
    }),
    [organization, queryString, prefix]
  );

  const link = useMemo(() => {
    const getDomain = () => {
      if (process.env.NODE_ENV === "production") return "culturehq.com";
      return "localhost:4000";
    };

    const domain = getDomain();
    const link = `${orgName.replace(/\s+/g, "-").toLowerCase()}.${domain}${prefix ? `/${prefix}` : ""}`;

    if (process.env.NODE_ENV === "production") return `https://${link}`;
    return `http://${link}`;
  }, [prefix, orgName]);

  const labels = useFieldsLabels(landingPage?.organization);

  useEffect(() => updateSeoTitle(organization), [organization, updateSeoTitle]);

  useEffect(
    () => {
      if (landingPage) {
        const tags = getTags(
          landingPage.values,
          landingPage.interests,
          landingPage.skills,
          landingPage.classYears,
          landingPage.clubs
        );
        const tagsIndex = {
          locations: {},
          companies: {},
          departments: {},
          degrees: {},
          programs: {},
          "class years": {}
        };
        (landingPage.companies || []).forEach(company => {
          tagsIndex.companies[company.id] = company;
        });
        (landingPage.departments || []).forEach(department => {
          tagsIndex.departments[department.id] = department;
        });
        (landingPage.locations || []).forEach(location => {
          tagsIndex.locations[location.id] = location;
        });
        (landingPage.degrees || []).forEach(degree => {
          tagsIndex.degrees[degree.id] = degree;
        });
        (landingPage.programs || []).forEach(program => {
          tagsIndex.programs[program.id] = program;
        });
        (landingPage.classYears || []).forEach(classYear => {
          tagsIndex["class years"][classYear.id] = classYear;
        });
        tags.forEach(tag => {
          if (!tagsIndex[tag.type]) {
            tagsIndex[tag.type] = {};
          }
          tagsIndex[tag.type][tag.id] = tag;
        });
        setTags(tags);

        const filters = [];
        Object.entries(queryString).forEach(element => {
          const categories = keyToCategory(element[0]);
          if ((Object.keys(tags).length !== 0 || Object.keys(categories).length !== 0)
            && Array.isArray(element[1])) {
            element[1].forEach(id => {
              filters.push({
                value: id,
                label: tagsIndex[categories.category][id]?.name,
                ...categories
              });
            });
          }
        });
        setSelectedFilters(filters);
      }
    },
    [landingPage, queryString]
  );

  useEffect(
    () => {
      setSelectedCompanies(selectedFilters.filter(filter => filter.category === "companies"));
      setSelectedDepartments(selectedFilters.filter(filter => filter.category === "departments"));
      setSelectedLocations(selectedFilters.filter(filter => filter.category === "locations"));
      setSelectedPrograms(selectedFilters.filter(filter => filter.category === "degrees"));
      setSelectedDegrees(selectedFilters.filter(filter => filter.category === "programs"));
      setSelectedFiltersNoDepNoLoc(selectedFilters.filter(filter => filter.category !== "departments" && filter.category !== "locations" && filter.category !== "companies" && filter.category !== "degrees" && filter.category !== "programs"));
    },
    [selectedFilters]
  );

  if (error && error.status === 403) {
    return <NotFound redirect="https://platform.culturehq.com/" />;
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  const getInvolvedIn = (showDepartmentsAndLocations = true) => {
    let filters = selectedFilters;
    if (!showDepartmentsAndLocations) {
      filters = selectedFiltersNoDepNoLoc;
    }
    return filters.map((filter, index) => {
      if (index === filters.length - 1) {
        return <span key={`${filter.category}-${filter.value}`} className="underlined">{filter.label}</span>;
      }

      if (index === filters.length - 2) {
        return (
          <span key={index}>
            <span key={`${filter.category}-${filter.value}`} className="underlined">{filter.label}</span>
            {filters.length !== 2 && <>,</>}
            {" "}
            and
            {" "}
          </span>
        );
      }
      return (
        <span key={index}>
          <span key={`${filter.category}-${filter.value}`} className="underlined">{filter.label}</span>
          ,
          {" "}
        </span>
      );
    });
  };

  const getCategoryInvolvedIn = selectedTags => (
    selectedTags.map((filter, index) => {
      if (index === selectedTags.length - 1) {
        return <span key={`${filter.category}-${filter.value}`} className="underlined">{filter.label}</span>;
      }

      if (index === selectedTags.length - 2) {
        return (
          <span key={index}>
            <span key={`${filter.category}-${filter.value}`} className="underlined">{filter.label}</span>
            {selectedTags.length !== 2 && <>,</>}
            {" "}
            or
            {" "}
          </span>
        );
      }

      return (
        <span key={index}>
          <span key={`${filter.category}-${filter.value}`} className="underlined">{filter.label}</span>
          ,
          {" "}
        </span>
      );
    })
  );

  return (
    <div className={classnames("LandingPageCallout", "LandingPageHomeCallout", orgName)}>
      <div className="LandingPageCallout--Preview">
        <div className="Preview">
          <HeaderImage landingPage={landingPage} orgName={orgName} />
          <div className="Preview-Body row">
            <div className="col-md-3 Preview-LeftColumn">
              <div className="Preview-LeftColumn__Container">
                {!insideIframe && (
                  <>
                    {landingPage?.logoUrl ? (
                      <img className="Preview-Logo col-xs-4 col-md-12" src={landingPage?.logoUrl || landingEmptyLogo} alt={t("Logo image")} />
                    ) : (
                      <div className="Preview-Logo col-xs-4 col-md-12" />
                    )}
                  </>
                )}
                <div
                  className={
                    classnames(
                      "Filters__button",
                      "toggle-filters",
                      "col-md-12",
                      "col-xs-8"
                    )
                  }
                >
                  <PlainButton onClick={onToggleOpenOnMobile} className="button toggle-filters">
                    <Icon icon="android-options" />
                    {openOnMobile ? <Trans>Hide Filters</Trans> : <Trans>Filter Content</Trans>}
                  </PlainButton>
                </div>
              </div>
              {isProgressive(orgName) && isTablet && (
                <h1 className="Preview-Header__Text visible-md">
                  {landingPage?.headerText}
                </h1>
              )}
              <div className="row">
                {landingPage?.primaryColor && (
                  <div className="Preview-Tags col-xs-8 col-md-12 hidden-md">
                    {tags.length > 0 && (
                      <Tags
                        entityFormat
                        tags={tags}
                        title={t("tags")}
                        color={landingPage.primaryColor}
                        selectedFilters={selectedFilters}
                        orgName={orgName}
                      />
                    )}
                    {landingPage.organization.isEducational ? (
                      <>
                        <Tags
                          type="programs"
                          tags={landingPage.programs}
                          title={t("programs")}
                          icon="ios-star-outline"
                          iconFilled="star"
                          color={landingPage.primaryColor}
                          queryKey="p"
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                        <Tags
                          type="degrees"
                          tags={landingPage.degrees}
                          title={t("degrees")}
                          icon="mortarboard"
                          iconFilled="mortarboard-filled"
                          color={landingPage.primaryColor}
                          queryKey="de"
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                      </>
                    ) : (
                      <>
                        <Tags
                          type="departments"
                          tags={landingPage.departments}
                          title={t(labels.departments.plural)}
                          icon="ios-people-outline"
                          iconFilled="ios-people"
                          color={landingPage.primaryColor}
                          queryKey="d"
                          selectedFilters={selectedFilters}
                          orgName={orgName}
                        />
                        {canViewCompaniesField(landingPage.organization) && (
                          <Tags
                            type="companies"
                            tags={landingPage.companies}
                            title={t(labels.companies.plural)}
                            icon="building"
                            iconFilled="building-filled"
                            color={landingPage.primaryColor}
                            queryKey="co"
                            selectedFilters={selectedFilters}
                            orgName={orgName}
                          />
                        )}
                        {!isDeloitte(orgName) && (
                          <Tags
                            type="locations"
                            tags={landingPage.locations}
                            title={t("locations")}
                            icon="loc-ol"
                            iconFilled="ios-location"
                            color={landingPage?.primaryColor || DEFAULT_COLOR}
                            queryKey="l"
                            selectedFilters={selectedFilters}
                            orgName={orgName}
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
                {landingPage && (
                  <div className="Preview-Tags Preview-Tags__Panel col-xs-12">
                    <Panel className={openOnMobile ? "open" : "closed"}>
                      <Panel.Body>
                        {tags.length > 0 && (
                          <Tags
                            entityFormat
                            tags={tags}
                            title={t("tags")}
                            color={landingPage?.primaryColor || DEFAULT_COLOR}
                            selectedFilters={selectedFilters}
                            orgName={orgName}
                          />
                        )}
                        {landingPage.organization.isEducational ? (
                          <>
                            <Tags
                              type="programs"
                              tags={landingPage.programs}
                              title={t("programs")}
                              icon="ios-star-outline"
                              iconFilled="ios-star-outline"
                              color={landingPage?.primaryColor || DEFAULT_COLOR}
                              queryKey="p"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                            <Tags
                              type="degrees"
                              tags={landingPage.degrees}
                              title={t("degrees")}
                              icon="mortarboard"
                              iconFilled="mortarboard-filled"
                              color={landingPage?.primaryColor || DEFAULT_COLOR}
                              queryKey="de"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                          </>
                        ) : (
                          <>
                            <Tags
                              type="departments"
                              tags={landingPage.departments}
                              title={t(labels.departments.plural)}
                              icon="ios-people-outline"
                              iconFilled="ios-people"
                              color={landingPage?.primaryColor || DEFAULT_COLOR}
                              queryKey="d"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                            {canViewCompaniesField(landingPage.organization) && (
                              <Tags
                                type="companies"
                                tags={landingPage.companies}
                                title={t(labels.companies.plural)}
                                icon="building"
                                iconFilled="building-filled"
                                color={landingPage?.primaryColor || DEFAULT_COLOR}
                                queryKey="co"
                                selectedFilters={selectedFilters}
                                orgName={orgName}
                              />
                            )}
                            <Tags
                              type="locations"
                              tags={landingPage.locations}
                              title={t("locations")}
                              icon="loc-ol"
                              iconFilled="ios-location"
                              color={landingPage.primaryColor}
                              queryKey="l"
                              selectedFilters={selectedFilters}
                              orgName={orgName}
                            />
                          </>
                        )}
                      </Panel.Body>
                    </Panel>
                  </div>
                )}
              </div>
            </div>
            <div className="col-md-9">
              {!isProgressive(orgName) && (
                <h1 className="Preview-Header__Text visible-xs">
                  {landingPage?.headerText}
                </h1>
              )}
              {((Object.values(queryString).length === 0 && !isCentrica(organization))
              || isTarget(organization)) && (
                <div className="Preview-Browser__Welcome">
                  {landingPage?.welcomeNote && (
                    <div className="Preview-Welcome" style={{ borderColor: `${landingPage?.primaryColor || DEFAULT_COLOR}` }}>
                      <Icon className="Preview-Welcome__Icon" color={landingPage?.primaryColor || DEFAULT_COLOR} icon="quote" />
                      <LazyEditorOutput className="Preview-Welcome__Note" output={landingPage?.welcomeNote || " "} showTags={false} landingPage />
                    </div>
                  )}
                </div>
              )}
              {fetching && selectedFilters.length === 0 ? (
                <>
                  <Loader loading />
                  <div className="StoryItemPlaceholder__Container">
                    <div className="StoryItemPlaceholder__Left">
                      <StoryItemPlaceholder />
                      <StoryItemPlaceholder />
                    </div>
                    <div className="StoryItemPlaceholder__Right">
                      <StoryItemPlaceholder />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {selectedFilters.length === 1 && fetching
                    && <Loader loading />}
                  {(selectedFilters.length !== 1 || !fetching)
                    && (
                      <>
                        <UsersSlider
                          queryParams={queryToOptions(queryString)}
                          organization={organization}
                          selectedFilters={selectedFilters}
                          isEducational={landingPage.organization.isEducational}
                        >
                          {selectedFilters.length === 0
                            ? <h2><Trans>Meet our people</Trans></h2>
                            : (
                              <h2>
                                <Trans>
                                  Meet people with selected tags: {getInvolvedIn()}
                                </Trans>
                              </h2>
                            )}
                        </UsersSlider>
                        <LandingPageStories
                          color={landingPage?.primaryColor}
                          insideIframe={insideIframe}
                          isEducational={landingPage.organization.isEducational}
                          organization={organization}
                          organizationId={landingPage.organization.id}
                          orgName={orgName}
                          queryParams={queryToOptions(queryString)}
                          usersIds={landingPage.usersIds}
                          selectedFilters={selectedFilters}
                        >
                          {selectedFilters.length === 0
                            ? <h2><Trans>See our stories</Trans></h2>
                            : (
                              <h2>
                                <Trans>See stories</Trans>
                                {" "}
                                {(selectedLocations.length > 0
                                  || selectedCompanies.length > 0
                                  || selectedDepartments.length > 0
                                  || selectedDegrees.length > 0
                                  || selectedPrograms.length > 0)
                                && (
                                  <>
                                    <Trans>from people in</Trans>
                                    {" "}
                                    {selectedDepartments.length > 0 && (
                                      <>
                                        {getCategoryInvolvedIn(selectedDepartments)}
                                        {" "}
                                        {selectedLocations.length > 0 && (
                                          <><Trans>and in</Trans>{" "}</>
                                        )}
                                      </>
                                    )}
                                    {selectedCompanies.length > 0 && (
                                      <>
                                        {getCategoryInvolvedIn(selectedCompanies)}
                                        {" "}
                                        {selectedLocations.length > 0 && (
                                          <><Trans>and in</Trans>{" "}</>
                                        )}
                                      </>
                                    )}
                                    {selectedPrograms.length > 0 && (
                                      <>
                                        {getCategoryInvolvedIn(selectedPrograms)}
                                        {" "}
                                        {selectedDegrees.length > 0 && (
                                          <><Trans>and in</Trans>{" "}</>
                                        )}
                                      </>
                                    )}
                                    {selectedLocations.length > 0 && (
                                      <>
                                        {getCategoryInvolvedIn(selectedLocations)}
                                        {" "}
                                      </>
                                    )}
                                    {selectedDegrees.length > 0 && (
                                      <>
                                        {getCategoryInvolvedIn(selectedDegrees)}
                                        {" "}
                                      </>
                                    )}
                                    {selectedFiltersNoDepNoLoc.length > 0 && (
                                      <><Trans>and</Trans>{" "}</>
                                    )}
                                  </>
                                )}
                                {selectedFiltersNoDepNoLoc.length > 0 && (
                                  <Trans>with selected tags: {getInvolvedIn(false)}</Trans>
                                )}
                              </h2>
                            )}
                        </LandingPageStories>
                      </>
                    )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


const LandingPageCallout = props => (
  <QueryStringProvider parsers={queryStringParsers}>
    <QueryStringConsumer>
      {queryStringContext => (
        <LandingPageCalloutContext
          {...props}
          {...queryStringContext}
        />
      )}
    </QueryStringConsumer>
  </QueryStringProvider>
);


export default LandingPageCallout;
